<template>
  <ContentTemplate title="Billings" :container-size-small="false">

    <div class="row mb-5">
      <div class="col-3">
        <InputElement
            type="text"
            name="searchInput"
            :with-icon=true
            icon-type="fal"
            icon="Search"
            placeholder="Search"
            v-model="searchValue"
            @input="filterTable"
        />
      </div>
      <div class="col-3">
        <FilterDropdownElement
            title="Filter by status"
            name="Status"
            v-model="selectedStatus"
            :options="filterStatus"
            type="buttonselect"
            icon="Filter"
            @change="filterTable"
        />
      </div>
      <div class="col-6 d-flex justify-content-end align-items-center">

        <ActionDropdownElement
            name=""
            :options="billingSettings"
            type="button"
            icon="EllipsisVertical"
            icon-type="fal"
            data-tooltip="Actions"
            data-tooltip-position="left"
            buttonclass="btn--ms btn--neutral btn--regular btn--icon"
        />

      </div>
    </div>

    <div class="row mb-5" v-if="orders.length === 0">
      <div class="col-12">
        <span class="tag--default tag--blue">
          No billing records available yet
        </span>
      </div>
    </div>

    <DataTable v-else :value="orders"
               :paginator="orders.length > 10"
               :rows="10"
               paginator-template=" PageLinks  "
               :rows-per-page-options="[10,20,50]"
               current-page-report-template="Showing {first} to {last} of {totalRecords}"
               class="datatable-invoice"
               responsive-layout="scroll"
               striped-rows
               removable-sort
    >
      <Column header="Invoice" class="datacol-1" field="id" :sortable="true">
        <template #body="slotProps">
          <div class="btn--link" @click="onActionDropdown({value: 'DOWNLOAD_PDF'}, slotProps.data)"
          >
            {{ slotProps.data.id }}
          </div>
        </template>
      </Column>
      <Column header="Amount" class="datacol-4">
        <template #body="slotProps">
          {{ slotProps.data.billingTotalPriceWithTaxes.toFixed(2) }}
        </template>
      </Column>
      <Column header="Currency" class="datacol-4">
        <template #body="slotProps">
          {{ slotProps.data.billingCurrencyCode }}
        </template>
      </Column>
      <Column header="Method" class="datacol-5">
        <template #body="slotProps">
          <div class="tag--default tag--neutral">
            {{ formatPaymentMethodName(slotProps.data.paymentMethodName) }}
          </div>
        </template>
      </Column>
      <Column header="Status" class="datacol-6">
        <template #body="slotProps">
          <div class="actions">
            <TagElement :class="getStatusClass(slotProps.data.orderStatusName)">
              {{ formatStatusName(slotProps.data.orderStatusName) }}
            </TagElement>
            <ActionDropdownElement
                title="Actions"
                :options="billingsActions"
                type="button"
                icon="EllipsisVertical"
                class="actions-icon"
                @change="onActionDropdown($event, slotProps.data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </ContentTemplate>
  <CancelSubscriptionPopup v-if="subscriptionInfo"
                           :show-popup="showCancelSubscriptionPopup"
                           :current-product-id="subscriptionInfo.productId"
                           :current-product-name="subscriptionInfo.productName"
                           @close="showCancelSubscriptionPopup = false"
  ></CancelSubscriptionPopup>
</template>
<script lang="ts">
import {defineComponent} from 'vue';
import ContentTemplate from "@shared/components/templates/content.template.vue";
import ActionDropdownElement
  from "@shared/components/elements/dropdown/action-dropdown.element.vue";
import {type BillingNs, type DropdownNs} from "@shared/types";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useBillingStore} from "@domains/settings/stores/billing.store";
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import TagElement from "@shared/components/elements/tag/tag.element.vue";
import {formatDate} from "@shared/helpers";
import FilterDropdownElement
  from "@shared/components/elements/dropdown/filter-dropdown.element.vue";
import {InputElement} from "@shared/components";
import {useGeneralStore} from "@shared/stores/general.store";
import CancelSubscriptionPopup from "@domains/settings/views/cancel-subscription.popup.vue";

export default defineComponent({
  name: 'BillingsView',
  components: {
    CancelSubscriptionPopup,
    InputElement,
    FilterDropdownElement,
    TagElement,
    ActionDropdownElement,
    ContentTemplate,
    DataTable,
    Column
  },
  setup() {
    // initialize the store
    const workspaceStore = useWorkspaceStore()
    const billingStore = useBillingStore()
    const generalStore = useGeneralStore()
    return {workspaceStore, billingStore, generalStore}
  },
  computed: {
    subscriptionInfo(): BillingNs.SubscriptionInfo {
      return this.billingStore.getSubscriptionInfo
    },
    billingSettings(): DropdownNs.DropdownOptionsItem[] {
      return [
        {
          name: "Cancel Subscription",
          value: "",
          closeOnSelect: true,
          callback: () => this.showCancelSubscriptionPopup = true
        }
      ];
    }
  },
  data() {
    return {
      orders: [] as BillingNs.PaymentOrder[],
      searchValue: '',
      selectedStatus: [] as DropdownNs.DropdownOptionsItem[],
      showCancelSubscriptionPopup: false,

      filterRoles: [
        {
          name: 'Waiting',
          inputName: 'waiting'
        },
        {
          name: 'Paid',
          inputName: 'paid'
        },
        {
          name: 'Failed',
          inputName: 'failed'
        }
      ] as DropdownNs.DropdownOptionsItem[],
      filterStatus: [
        {
          name: 'Processed',
          value: 'Processed'
        },
        {
          name: 'Waiting',
          value: 'Waiting'
        },
        {
          name: 'Refunded',
          value: 'Refunded'
        },
        {
          name: 'Chargeback',
          value: 'Chargeback'
        },
        {
          name: 'Canceled',
          value: 'Canceled'
        }
      ] as DropdownNs.DropdownOptionsItem[],
      billingsActions: [
        {
          name: 'Open Invoice',
          iconType: 'fal',
          icon: 'Receipt',
          value: 'DOWNLOAD_PDF'
        }
        // ,
        // {
        //   name: 'Pay Now',
        //   iconType: 'fal',
        //   icon: 'ArrowRight',
        //   value: 'PAY_NOW'
        // }
      ] as DropdownNs.DropdownOptionsItem[]

    };
  },
  beforeMount() {
    this.billingStore.loadSubscriptionInfo(this.workspaceStore.getSelectedWorkspaceId).then((response) => {
      this.orders = response.orders;
    });
  },
  methods: {
    formatDate,
    getStatusClass(status: string) {
      // 1 – Waiting, 2 – Canceled, 3 –Refunded, 4 – Chargeback, 5 – Processed
      if (status === 'PROCESSED') {
        return 'tag--blue'
      }
      if (status === 'WAITING') {
        return 'tag--neutral'
      }
      if (status === 'REFUNDED') {
        return 'tag--orange'
      }
      if (status === 'CHARGEBACK') {
        return 'tag--teal'
      }
      if (status === 'CANCELED') {
        return 'tag--red'
      }
    },
    onActionDropdown(event: any, data: any) {
      console.log(event)
      if (event.value === 'DOWNLOAD_PDF') {
        window.open(data.invoiceUrl, '_blank');
      }
    },
    filterTable() {
      let filteredList = this.subscriptionInfo.orders
      if (this.searchValue) {
        filteredList = filteredList.filter((item: BillingNs.PaymentOrder) => (item.id.toString().startsWith(this.searchValue.toLowerCase()) ||
            formatDate(item.createdAt).toLowerCase().includes(this.searchValue.toLowerCase())))
      }
      if (this.selectedStatus && this.selectedStatus.length > 0) {
        filteredList = filteredList.filter((item: BillingNs.PaymentOrder) => (this.selectedStatus.some((status) => (status.value === item.orderStatusName))))
      }
      this.orders = filteredList
    },
    formatPaymentMethodName(name: string): string {
      if (!name) return '';
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },
    formatStatusName(status: string): string {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
    // fetchInfoData() {
    //   store.dispatch('actionGetCustomerCards')
    //   .then((response: BillingNamespace.CardInfoInterface[]) => {
    //     if (response && response.length) {
    //       this.customerCard = response[0];
    //     }
    //   });
    //
    //   store.dispatch('actionGetBillingAddress', this.gotSelectedWorkspaceId)
    //   .then((response: BillingNamespace.BillingAddressInterface[]) => {
    //     if (response && response.length) {
    //       this.billingAddress = response[0];
    //     }
    //   });
    //
    //   store.dispatch('actionGetSubscriptionInfos')
    //   .then((response: BillingNamespace.SubscriptionInfoInterface[]) => {
    //     if (response && response.length) {
    //       this.subscriptionInfo = response[0];
    //     }
    //   });
    // },
    //
    // deletePaymentMethod() {
    //   if (this.customerCard.cardId) {
    //     store.dispatch('actionDeletePaymentMethod', this.customerCard.cardId)
    //     .then((response) => {
    //       if (response) {
    //         this.fetchInfoData();
    //       }
    //     });
    //   }
    // },
    //
    // deleteBillingAddress() {
    //   if (this.customerCard.cardId) {
    //     store.dispatch('actionDeleteBillingAddress', this.billingAddress.addressId)
    //     .then((response) => {
    //       if (response) {
    //         this.fetchInfoData();
    //       }
    //     });
    //   }
    // },
    //
    // hasAPaymentMethod(): boolean {
    //   return !_.isEmpty(this.customerCard);
    // },
    //
    // hasABillingAddress(): boolean {
    //   return !_.isEmpty(this.billingAddress);
    // },
    //
    // toBillingAddressDetails() {
    //   this.$router.push({name: SettingNamespace.SettingRouteEnums.BILLINGADDRESS});
    // },
    //
    // toPaymentDetails() {
    //   this.$router.push({name: SettingNamespace.SettingRouteEnums.PAYMENTMETHOD});
    // }

  }
});
</script>

<style lang="scss">

.datatable-invoice {
  td, th {
    padding: var(--space-8);
    font-size: var(--fs-12);
  }

  th {
    font-size: var(--fs-12);
    font-weight: 500;
    color: var(--text-neutral);
  }

  .datacol-1 {
    width: 100px;
  }

  .invoice {
    color: var(--text-dark);
    font-weight: 600;
  }

  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .p-datatable-wrapper {

    .p-datatable-tbody {
      tr {
        font-size: var(--fs-14);
        color: var(--text-neutral);
        background: var(--transparent);
        border: 1px solid var(--transparent);
        border-radius: $br-sm;


        &:hover {
          border-radius: $br-sm;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          box-shadow: var(--shadow-lg);
          z-index: 1;
          position: relative;
        }

        td:first-child {
          border-top-left-radius: $br-sm;
          border-bottom-left-radius: $br-sm;
        }

        td:last-child {
          border-top-right-radius: $br-sm;
          border-bottom-right-radius: $br-sm;
        }
      }
    }
  }
}

.btn--link {
  color: var(--text-accent);
  text-decoration: underline;
  cursor: pointer;
}

</style>
